/**
 * Research Mode Configurations
 * 
 * Defines the specific configurations for each research mode/blueprint.
 */

import type { ResearchMode, ResearchModeConfig, AgentRole } from './researchModes';

// Financial Analyst Mode - Agent Roles
const FINANCIAL_AGENTS: AgentRole[] = [
  {
    id: 'risk-analyst',
    name: 'Risk Analyst',
    description: 'Identifies potential risks, red flags, and downside scenarios',
    perspective: 'bearish',
    icon: 'shield-alert',
    color: 'text-red-400',
  },
  {
    id: 'value-investor',
    name: 'Value Investor',
    description: 'Evaluates intrinsic value, fundamentals, and margin of safety',
    perspective: 'neutral',
    icon: 'scale',
    color: 'text-blue-400',
  },
  {
    id: 'growth-strategist',
    name: 'Growth Strategist',
    description: 'Analyzes growth potential, market opportunity, and upside catalysts',
    perspective: 'bullish',
    icon: 'trending-up',
    color: 'text-green-400',
  },
  {
    id: 'market-technician',
    name: 'Market Technician',
    description: 'Examines price action, momentum, and market sentiment',
    perspective: 'neutral',
    icon: 'activity',
    color: 'text-purple-400',
  },
  {
    id: 'macro-economist',
    name: 'Macro Economist',
    description: 'Considers broader economic factors and sector dynamics',
    perspective: 'neutral',
    icon: 'globe',
    color: 'text-cyan-400',
  },
];

// Strategic Consultant Mode - Agent Roles (Consultant Personas)
const STRATEGIC_AGENTS: AgentRole[] = [
  {
    id: 'strategy-lead',
    name: 'Strategy Lead',
    description: 'Drives hypothesis formation and synthesizes workstream findings',
    perspective: 'strategic',
    icon: 'target',
    color: 'text-blue-500',
  },
  {
    id: 'market-analyst',
    name: 'Market Analyst',
    description: 'Analyzes TAM/SAM/SOM, customer segments, and demand drivers',
    perspective: 'analytical',
    icon: 'bar-chart-2',
    color: 'text-cyan-400',
  },
  {
    id: 'competitive-intelligence',
    name: 'Competitive Intelligence',
    description: 'Maps competitive landscape, moats, and differentiation opportunities',
    perspective: 'investigative',
    icon: 'search',
    color: 'text-amber-400',
  },
  {
    id: 'business-model-expert',
    name: 'Business Model Expert',
    description: 'Evaluates unit economics, revenue models, and scalability',
    perspective: 'quantitative',
    icon: 'calculator',
    color: 'text-green-400',
  },
  {
    id: 'operations-risk',
    name: 'Operations & Risk',
    description: 'Assesses execution risks, operational complexity, and mitigations',
    perspective: 'critical',
    icon: 'shield-alert',
    color: 'text-red-400',
  },
];

// General Research Mode - Balanced Agents for general queries
const GENERAL_AGENTS: AgentRole[] = [
  {
    id: 'researcher',
    name: 'Researcher',
    description: 'Gathers and analyzes relevant information',
    perspective: 'neutral',
    icon: 'search',
    color: 'text-blue-400',
  },
  {
    id: 'analyst',
    name: 'Analyst',
    description: 'Evaluates findings and draws conclusions',
    perspective: 'analytical',
    icon: 'bar-chart-2',
    color: 'text-green-400',
  },
  {
    id: 'critic',
    name: 'Critic',
    description: 'Challenges assumptions and identifies weaknesses',
    perspective: 'critical',
    icon: 'alert-triangle',
    color: 'text-amber-400',
  },
];

// Auto-detect Mode - Uses LLM to determine best approach
const AUTO_AGENTS: AgentRole[] = [
  {
    id: 'auto-researcher',
    name: 'Adaptive Researcher',
    description: 'Automatically adapts research approach based on query',
    perspective: 'adaptive',
    icon: 'sparkles',
    color: 'text-verdict-primary',
  },
];

// Mode Configurations
export const RESEARCH_MODE_CONFIGS: Record<ResearchMode, ResearchModeConfig> = {
  auto: {
    id: 'auto',
    name: 'Auto Detect',
    shortName: 'Auto',
    description: 'Automatically picks the best approach for your question',
    icon: 'sparkles',
    color: 'verdict-primary',
    primaryAgents: AUTO_AGENTS,
    debateFocus: 'Validity of findings',
    debateFraming: { sideA: 'Supporting Evidence', sideB: 'Counter Evidence' },
    priorityDataSources: ['web', 'news', 'academic'],
    verdictOutputType: 'Key Findings',
    exportFormats: ['pdf', 'markdown'],
    showKillSheet: false,
    showDataTimestamps: false,
  },
  general: {
    id: 'general',
    name: 'General Research',
    shortName: 'General',
    description: 'Balanced research on any topic with fact-checking and debate',
    icon: 'book-open',
    color: 'purple-500',
    primaryAgents: GENERAL_AGENTS,
    debateFocus: 'Accuracy and completeness of findings',
    debateFraming: { sideA: 'Supporting Evidence', sideB: 'Counter Evidence' },
    priorityDataSources: ['web', 'news', 'academic', 'industry-reports'],
    verdictOutputType: 'Research Summary',
    exportFormats: ['pdf', 'markdown'],
    showKillSheet: false,
    showDataTimestamps: false,
  },
  financial: {
    id: 'financial',
    name: 'Financial Deep Dive',
    shortName: 'Financial',
    description: 'Bull vs. bear analysis with adversarial debate on investments',
    icon: 'trending-up',
    color: 'green-500',
    primaryAgents: FINANCIAL_AGENTS,
    debateFocus: 'Is the valuation justified?',
    debateFraming: { sideA: 'Bull Case', sideB: 'Bear Case' },
    priorityDataSources: ['finnhub', 'sec-edgar', 'earnings-calls', 'financial-news'],
    verdictOutputType: 'Kill Sheet',
    exportFormats: ['pdf', 'csv', 'json'],
    showKillSheet: true,
    showDataTimestamps: true,
  },
  strategic: {
    id: 'strategic',
    name: 'Strategic Consultant',
    shortName: 'Strategic',
    description: 'Consulting-style analysis with stress-tested recommendations',
    icon: 'target',
    color: 'blue-500',
    primaryAgents: STRATEGIC_AGENTS,
    debateFocus: 'Will the strategy succeed?',
    debateFraming: { sideA: 'Why This Strategy Will Succeed', sideB: 'Why It Will Fail / Why Now Is Wrong' },
    priorityDataSources: ['market-research', 'industry-reports', 'competitor-analysis', 'sec-edgar'],
    verdictOutputType: 'Strategic Deliverable',
    exportFormats: ['pdf', 'markdown', 'json'],
    showKillSheet: false,
    showDataTimestamps: true,
    // Alpha tag: Mode is under development, excluded from auto-detection
    // Users must manually select this mode to use it
    tags: ['alpha'],
  },
};

// Helper to get agent role by model name mapping
export function getAgentRoleForModel(
  mode: ResearchMode,
  _modelName: string,
  agentIndex: number
): AgentRole {
  const config = RESEARCH_MODE_CONFIGS[mode];
  // Cycle through available agents based on index
  // Note: _modelName reserved for future model-specific role mapping
  const roleIndex = agentIndex % config.primaryAgents.length;
  return config.primaryAgents[roleIndex];
}

// Helper to get mode config
export function getModeConfig(mode: ResearchMode): ResearchModeConfig {
  return RESEARCH_MODE_CONFIGS[mode];
}

// Helper to check if a mode has a specific tag
export function modeHasTag(mode: ResearchMode, tag: string): boolean {
  const config = RESEARCH_MODE_CONFIGS[mode];
  return config.tags?.includes(tag) ?? false;
}

// Helper to check if a mode is in alpha
export function isAlphaMode(mode: ResearchMode): boolean {
  return modeHasTag(mode, 'alpha');
}

